import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
  type Mutation,
} from '@tanstack/react-query';
import { isStepUpError } from '@truecare/api-client';
import './styles/globals.css';
import { routeTree } from './routeTree.gen';
import { TotpStepUpModalProvider } from './components/TotpStepUpModalProvider';
import { AdminPending } from './components/AdminPending';

/**
 * Phase 14b-1-5 — MutationCache global onError → step-up modal bridge.
 *
 * Chicken/egg: MutationCache is created at QueryClient construction
 * (before any React tree mounts); the `<TotpStepUpModalProvider>` lives
 * in the tree. The solution is an indirection ref — MutationCache reads
 * `enqueueStepUpRef.current` lazily on each error event, and the
 * Provider sets it via `onBind` once it has mounted. Do NOT refactor
 * the indirection away; without it the global onError can't dispatch
 * into React state.
 *
 * Lookup-on-error (not lookup-at-bind) also gives StrictMode's double-
 * mount the chance to land cleanly — the second mount's enqueue wins.
 */
const enqueueStepUpRef: {
  current: ((mutation: Mutation<unknown, unknown, unknown, unknown>) => void) | null;
} = { current: null };

// 14b-1 post-audit M6 — `isStepUpError` is the HMR-safe discriminator
// (instanceof check + structural fallback). Imported from
// @truecare/api-client so the discrimination logic lives next to the
// error class itself; refactors that rename `.status` or `.reason`
// happen in one place — protected by api-client's errors.spec.ts.

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) => {
      if (isStepUpError(error)) {
        // Lookup happens at error time so the most-recently-bound
        // provider wins (covers HMR + StrictMode).
        enqueueStepUpRef.current?.(mutation);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Router basepath tracks Vite's `base` (import.meta.env.BASE_URL). Normally
// '/' (→ undefined, TanStack's default); the demo/tunnel path serves admin
// under `<portal-origin>/admin` (base '/admin/') so it shares an origin — and
// thus the session cookie — with the portal. See vite.config.ts `base`.
const routerBasepath =
  import.meta.env.BASE_URL.replace(/\/+$/, '') || undefined;

// `defaultPendingComponent` shows the app shell's loading state while a route's
// `beforeLoad` (the /auth/me gate) is in flight — replaces the blank-white
// cold-load flash. `defaultPendingMs: 0` shows it immediately on the initial
// load rather than after TanStack's default 1s grace.
const router = createRouter({
  routeTree,
  basepath: routerBasepath,
  defaultPendingComponent: AdminPending,
  defaultPendingMs: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('No #root element found in index.html');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TotpStepUpModalProvider
        onBind={(enqueue) => {
          enqueueStepUpRef.current = enqueue;
        }}
      >
        <RouterProvider router={router} />
      </TotpStepUpModalProvider>
    </QueryClientProvider>
  </StrictMode>,
);
